import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../index';
import { Campaign, TimeFilterOptions } from '../types';

interface UseCampaignsOptions extends TimeFilterOptions {}

interface CreateCampaignData {
  name: string;
  tag: string;
  notes: string;
  templateId: number | null;
  scheduledAt: string | null;
  sendFromMail: string;
  sendFromName: string;
  trackingUrl: string;
}

export const useCampaigns = (options: UseCampaignsOptions = {}) => {
  const [data, setData] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (options.showAll) {
        params.showAll = 'true';
      }

      const result = await apiClient.get<Campaign[]>('/api/campaigns', params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  }, [options.showAll]);

  const createCampaign = useCallback(async (campaignData: CreateCampaignData) => {
    const result = await apiClient.post<Campaign>('/api/campaigns', campaignData);
    await fetchCampaigns();
    return result;
  }, [fetchCampaigns]);

  const updateCampaign = useCallback(async (id: number, campaignData: CreateCampaignData) => {
    const result = await apiClient.put<Campaign>(`/api/campaigns/${id}`, campaignData);
    await fetchCampaigns();
    return result;
  }, [fetchCampaigns]);

  const deleteCampaign = useCallback(async (id: number) => {
    await apiClient.delete(`/api/campaigns/${id}`);
    await fetchCampaigns();
  }, [fetchCampaigns]);

  const downloadCsv = useCallback(async (id: number) => {
    const response = await fetch(`${apiClient['baseUrl']}/api/campaigns/${id}/csv`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    
    if (!response.ok) throw new Error('Download failed');
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign-${id}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, []);

  const sendTestEmail = useCallback(async (testData: any) => {
    const { emails, ...restData } = testData;
    
    if (emails && Array.isArray(emails)) {
      if (emails.length === 0) {
        throw new Error('At least one email is required');
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter(e => !emailRegex.test(e));
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid email format: ${invalidEmails.join(', ')}`);
      }
      
      await Promise.all(
        emails.map(emailAddr => apiClient.post('/api/campaigns/test-draft', { ...restData, email: emailAddr }))
      );
      return { message: `Test emails sent to ${emails.length} recipient(s) successfully` };
    } else {
      return apiClient.post('/api/campaigns/test-draft', testData);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  return {
    data,
    loading,
    error,
    refetch: fetchCampaigns,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    downloadCsv,
    sendTestEmail,
  };
};